import React, { useState, useEffect, useRef } from 'react';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Delete, Download, Eye, Plus, Trash2 } from 'react-feather';
import { Modal, Button, ConfigProvider, Popconfirm, message } from 'antd';
import QRCode from 'qrcode';

// Reuse A QUI DE DROIT assets and styles (same header and separator)
import edaplogo from './img/edaplogo.png';
import unilislogo from './img/unilislogo.png';
import rectorSig from './img/rector_sig.png';
import './aquidedroit.css';

const AttestationGrade = ({ app }) => {
  const [formData, setFormData] = useState({
    // Title controls
    diplomeType: '', // Doctorat | Master | HDR | Licence
    // Header No/réf
    numeroRef: '',
    
    // Language
    language: 'Français', // Français | English | Français + English

    // Identity
    genre: 'H', // H (Homme) | F (Femme)
    noms: '',
    dateNaissance: '',
    lieuNaissance: '',
    pays: '',
    paysConnector: 'au', // au | en | à la | aux

    // School info
    matricule: '',
    cycleDescription: '',
    filiere: '', // ex: Sciences Économiques et de Gestion
    filiereEn: '', // English for bilingual
    specialite: '', // ex: Finance
    specialiteEn: '', // English for bilingual

    // Thesis
    theseTitre: '',
    theseTitreEn: '', // English title (only for bilingual)

    // Jury
    president: '',
    directeur: '',
    rapporteur: '',
    membres: [''], // allow adding more

    // Decision
    mention: '',
    mentionEn: '', // English for bilingual
    mentionEnCustom: '', // for custom English mention
    gradeTexte: '', // free text for grade confirmation at the end
    gradePreset: '', // for dropdown selection
    gradeTexteEn: '', // English grade text
    gradePresetEn: '', // English grade preset

    // Meta
    anneeAcademique: '',
    dateEmission: '',
    includeSignature: true,
  });

  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // date | name
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const db = getDatabase(app);

  // Load from Firebase
  useEffect(() => {
    const documentsRef = ref(db, 'attestation-grade/UNCR/');
    onValue(documentsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const arr = Object.keys(data).map((key) => ({ id: key, ...data[key] }));
        setDocuments(arr);
      } else {
        setDocuments([]);
      }
    });
  }, [db]);

  // QR code
  useEffect(() => {
    const generate = async () => {
      if (!selectedDocument) return;
      let qrData = `ATTESTATION DE GRADE - UNILIS\n`;
      Object.keys(selectedDocument).forEach((key) => {
        if (key !== 'createdAt' && selectedDocument[key]) {
          qrData += `${key}: ${Array.isArray(selectedDocument[key]) ? selectedDocument[key].join(', ') : selectedDocument[key]}\n`;
        }
      });
      qrData += `Status: Document Verified\nIssued by: UNILIS - Université de Lisala`;
      try {
        const qr = await QRCode.toDataURL(qrData, { width: 120, margin: 1, color: { dark: '#000000', light: '#FFFFFF' } });
        setQrCodeData(qr);
      } catch (e) {
        console.error('QR error', e);
      }
    };
    if (selectedDocument) generate();
  }, [selectedDocument]);

  const handleChange = (e, idx) => {
    const { name, value } = e.target;
    if (name === 'membre' && typeof idx === 'number') {
      const copy = [...formData.membres];
      copy[idx] = value;
      setFormData((prev) => ({ ...prev, membres: copy }));
    } else if (name === 'gradePreset') {
      // When preset changes, update gradeTexte accordingly
      if (value === 'custom') {
        setFormData((prev) => ({ ...prev, gradePreset: value, gradeTexte: '' }));
      } else {
        setFormData((prev) => ({ ...prev, gradePreset: value, gradeTexte: value }));
      }
    } else if (name === 'gradePresetEn') {
      // When English preset changes, update gradeTexteEn accordingly
      if (value === 'custom') {
        setFormData((prev) => ({ ...prev, gradePresetEn: value, gradeTexteEn: '' }));
      } else {
        setFormData((prev) => ({ ...prev, gradePresetEn: value, gradeTexteEn: value }));
      }
    } else if (name === 'mentionEn') {
      // When English mention changes, handle custom option
      if (value === 'custom') {
        setFormData((prev) => ({ ...prev, mentionEn: value, mentionEnCustom: '' }));
      } else {
        setFormData((prev) => ({ ...prev, mentionEn: value, mentionEnCustom: '' }));
      }
    } else if (name === 'mentionEnCustom') {
      // Update custom mention text
      setFormData((prev) => ({ ...prev, mentionEnCustom: value }));
    } else if (name === 'gradeTexteEn') {
      // Update custom English grade text
      setFormData((prev) => ({ ...prev, gradeTexteEn: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const addMembre = () => {
    setFormData((prev) => ({ ...prev, membres: [...prev.membres, ''] }));
  };

  const removeMembre = (idx) => {
    setFormData((prev) => ({ ...prev, membres: prev.membres.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // minimal validation
    const required = ['noms', 'dateNaissance', 'lieuNaissance', 'pays', 'matricule', 'filiere', 'specialite', 'theseTitre', 'anneeAcademique', 'dateEmission'];
    for (const f of required) {
      if (!formData[f] || String(formData[f]).trim() === '') {
        alert(`${f} est obligatoire!`);
        return;
      }
    }
    const sanitized = formData.noms.replace(/[.#$\[\]]/g, '_').replace(/\s+/g, '_');
    const path = ref(db, `attestation-grade/UNCR/${sanitized}_${Date.now()}`);
    set(path, { ...formData, createdAt: new Date().toISOString() })
      .then(() => message.success('Document créé avec succès'))
      .catch((err) => { console.error(err); message.error('Erreur lors de la création'); });

    // reset minimal fields but keep defaults
    setFormData((prev) => ({
      ...prev,
      numeroRef: '',
      noms: '',
      dateNaissance: '',
      lieuNaissance: '',
      pays: '',
      matricule: '',
      cycleDescription: '',
      filiere: '',
      specialite: '',
      theseTitre: '',
      theseTitreEn: '',
      president: '',
      directeur: '',
      rapporteur: '',
      membres: [''],
      gradeTexte: '',
      gradePreset: '',
      anneeAcademique: '',
      dateEmission: '',
    }));
  };

  const showModal = (doc) => { setSelectedDocument(doc); setModalVisible(true); };
  const closeModal = () => { setModalVisible(false); setSelectedDocument(null); };

  const delDoc = (id) => {
    remove(ref(db, `attestation-grade/UNCR/${id}`))
      .then(() => message.success('Document supprimé'))
      .catch(() => message.error('Suppression échouée'));
  };

  const downloadPDF = async () => {
    if (!selectedDocument) return;
    const el = document.getElementById('attestation-grade-document');
    if (!el) return;

    const showBoth = selectedDocument.language === 'Français + English';
    
    try {
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Get all document pages (if bilingual, there will be 2 divs with class 'aquidedroit-document')
      const pages = el.querySelectorAll('.aquidedroit-document');
      
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        
        // Clone and style the page
        const clone = page.cloneNode(true);
        clone.style.cssText = `
          width: 21cm !important;
          height: auto !important;
          min-height: 29.7cm !important;
          margin: 0 !important;
          padding: 2cm 0.5cm !important;
          background: white !important;
          position: relative !important;
          box-sizing: border-box !important;
          font-family: 'Times New Roman', serif !important;
          overflow: visible !important;
          page-break-after: avoid !important;
        `;

        const tmp = document.createElement('div');
        tmp.style.cssText = 'position: fixed; top: -9999px; left: -9999px; z-index: -1; width: 21cm;';
        tmp.appendChild(clone);
        document.body.appendChild(tmp);

        // Wait for images to load
        const images = clone.querySelectorAll('img');
        await Promise.all(Array.from(images).map(img => new Promise((res) => { 
          if (img.complete) res(); 
          else { img.onload = res; img.onerror = res; } 
        })));
        await new Promise((r) => setTimeout(r, 400));

        // Create canvas for this page
        const canvas = await html2canvas(clone, { 
          scale: 2, 
          useCORS: true, 
          allowTaint: true, 
          backgroundColor: '#FFFFFF',
          windowWidth: clone.scrollWidth,
          windowHeight: clone.scrollHeight
        });

        // Calculate scaling to fit the page
        const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
        const w = canvas.width * ratio;
        const h = canvas.height * ratio;
        const x = (pageWidth - w) / 2;
        const y = (pageHeight - h) / 2;

        // Add new page if not the first page
        if (i > 0) {
          pdf.addPage();
        }

        // Add image to PDF
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, w, h);

        // Clean up
        document.body.removeChild(tmp);
      }

      pdf.save(`${selectedDocument.noms?.replace(/\s+/g, '_') || 'document'}_ATTESTATION_DE_GRADE.pdf`);
    } catch (e) {
      console.error(e);
      message.error('Erreur lors de la génération du PDF');
    }
  };

  return (
    <div className="aquidedroit-container">
      <div className="aquidedroit-form-section" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img src={edaplogo} alt="Logo EDAP" className="aquidedroit-form-logo" />
          <h2>Formulaire ATTESTATION DE GRADE</h2>
        </div>

        <div className="card-form" style={{ padding: '40px', background: '#ffffff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '100%', boxSizing: 'border-box' }}>
          <form onSubmit={handleSubmit}>
            {/* Document Configuration */}
            <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#2c3e50', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '10px', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px' }}>📄</span> Configuration du Document
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <select name="language" value={formData.language} onChange={handleChange} style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }}>
                  <option value="Français">Français</option>
                  <option value="English">English</option>
                  <option value="Français + English">Français + English</option>
                </select>
                <select name="diplomeType" value={formData.diplomeType} onChange={handleChange} style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }}>
                  <option value="">-- {formData.language === 'English' ? 'Degree type' : 'Type de diplôme'} --</option>
                  <option value="Licence">{formData.language === 'English' ? 'Bachelor' : 'Licence'}</option>
                  <option value="Master">Master</option>
                  <option value="Doctorat">{formData.language === 'English' ? 'Doctorate' : 'Doctorat'}</option>
                  <option value="HDR">HDR</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginTop: '20px' }}>
                <input type="text" name="numeroRef" value={formData.numeroRef} onChange={handleChange} placeholder="No/réf (optionnel)" style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Candidate Information */}
            <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#2c3e50', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '10px', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px' }}>👤</span> Informations du Candidat
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <select name="genre" value={formData.genre} onChange={handleChange} style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }}>
                  <option value="H">Homme (H)</option>
                  <option value="F">Femme (F)</option>
                </select>
                <input type="text" name="noms" value={formData.noms} onChange={handleChange} placeholder="Nom complet *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
                <input type="text" name="dateNaissance" value={formData.dateNaissance} onChange={handleChange} placeholder="Date de naissance (jj/mm/aaaa) *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
                <input type="text" name="lieuNaissance" value={formData.lieuNaissance} onChange={handleChange} placeholder="Lieu de naissance *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '20px', marginTop: '20px' }}>
                <select name="paysConnector" value={formData.paysConnector} onChange={handleChange} style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }}>
                  <option value="au">au</option>
                  <option value="en">en</option>
                  <option value="à la">à la</option>
                  <option value="aux">aux</option>
                </select>
                <input type="text" name="pays" value={formData.pays} onChange={handleChange} placeholder="Pays *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
                <input type="text" name="matricule" value={formData.matricule} onChange={handleChange} placeholder="Numéro de matricule *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Academic Program */}
            <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#2c3e50', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '10px', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px' }}>🎓</span> Programme Académique {formData.language === 'Français + English' && '(Français)'}
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <input type="text" name="cycleDescription" value={formData.cycleDescription} onChange={handleChange} placeholder="Cycle (ex: doctoral de 3 années) *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
                <input type="text" name="anneeAcademique" value={formData.anneeAcademique} onChange={handleChange} placeholder="Année académique *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
                <input type="text" name="filiere" value={formData.filiere} onChange={handleChange} placeholder="Discipline/Filière (Français) *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
                <input type="text" name="specialite" value={formData.specialite} onChange={handleChange} placeholder="Spécialité (Français) *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginTop: '20px' }}>
                <input type="text" name="theseTitre" value={formData.theseTitre} onChange={handleChange} placeholder={`Titre ${formData.diplomeType === 'Doctorat' ? 'de la Thèse' : 'du Mémoire'} (Français) *`} required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Academic Program - English (only for bilingual) */}
            {formData.language === 'Français + English' && (
              <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#2c3e50', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '10px', display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px' }}>🎓</span> Academic Program (English)
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <input type="text" name="filiereEn" value={formData.filiereEn} onChange={handleChange} placeholder="Discipline/Field (English) *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
                  <input type="text" name="specialiteEn" value={formData.specialiteEn} onChange={handleChange} placeholder="Specialization (English) *" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginTop: '20px' }}>
                  <input type="text" name="theseTitreEn" value={formData.theseTitreEn} onChange={handleChange} placeholder={`${formData.diplomeType === 'Doctorat' ? 'Thesis' : 'Dissertation'} Title (English) *`} required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white', boxSizing: 'border-box' }} />
                </div>
              </div>
            )}

            {/* Jury Composition */}
            <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#2c3e50', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '10px', display: 'flex', alignItems: 'center' }}>
                Composition du Jury
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                <input type="text" name="president" value={formData.president} onChange={handleChange} placeholder="Président du jury" style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }} />
                <input type="text" name="directeur" value={formData.directeur} onChange={handleChange} placeholder="Directeur de thèse" style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }} />
                <input type="text" name="rapporteur" value={formData.rapporteur} onChange={handleChange} placeholder="Rapporteur" style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }} />
                {formData.membres.map((m, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="text" name="membre" value={m} onChange={(e) => handleChange(e, idx)} placeholder={`Membre du jury ${idx + 1}`} style={{ flex: 1, boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }} />
                    {formData.membres.length > 1 && (
                      <button type="button" onClick={() => removeMembre(idx)} title="Supprimer" style={{ border: 'none', background: '#dc3545', color: 'white', padding: '10px 14px', borderRadius: '6px', cursor: 'pointer', transition: 'background 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addMembre} style={{ width: '100%', boxSizing: 'border-box', background: '#6c757d', color: 'white', border: 'none', padding: '12px 16px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '500', marginTop: '8px', transition: 'background 0.2s' }} onMouseOver={(e) => e.target.style.background = '#5a6268'} onMouseOut={(e) => e.target.style.background = '#6c757d'}>
                  <Plus size={16} style={{ marginRight: 8 }} /> Ajouter un membre du jury
                </button>
              </div>
            </div>

            {/* Evaluation & Grade - French */}
            {(formData.language === 'Français' || formData.language === 'Français + English') && (
              <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#2c3e50', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '10px', display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px' }}>⭐</span> Évaluation et Grade {formData.language === 'Français + English' && '(Français)'}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                  <select name="mention" value={formData.mention} onChange={handleChange} style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }}>
                    <option value="">-- Sélectionner la mention --</option>
                    <option value="Très Honorable avec félicitations du jury">Très Honorable avec félicitations du jury</option>
                    <option value="Très Honorable">Très Honorable</option>
                    <option value="Honorable">Honorable</option>
                    <option value="Bien">Bien</option>
                    <option value="Assez Bien">Assez Bien</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: formData.gradePreset === 'custom' || (formData.gradeTexte && !['Licencié', 'Docteur (PhD)', 'Docteur (Ph.D)', 'Master (M.Sc)', 'Master (M.A)', 'HDR'].includes(formData.gradeTexte)) ? '1fr 1fr' : '1fr', gap: '20px', marginTop: '20px' }}>
                  <select name="gradePreset" value={formData.gradePreset} onChange={handleChange} style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }}>
                    <option value="">-- Sélectionner le grade --</option>
                    <option value="Licencié">Licencié</option>
                    <option value="Master (M.Sc)">Master (M.Sc)</option>
                    <option value="Master (M.A)">Master (M.A)</option>
                    <option value="Docteur (PhD)">Docteur (PhD)</option>
                    <option value="Docteur (Ph.D)">Docteur (Ph.D)</option>
                    <option value="HDR">HDR</option>
                    <option value="custom">Personnaliser...</option>
                  </select>
                  {(formData.gradePreset === 'custom' || (formData.gradeTexte && !['Licencié', 'Docteur (PhD)', 'Docteur (Ph.D)', 'Master (M.Sc)', 'Master (M.A)', 'HDR'].includes(formData.gradeTexte))) && (
                    <input type="text" name="gradeTexte" value={formData.gradeTexte} onChange={handleChange} placeholder="Grade personnalisé *" required style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }} />
                  )}
                </div>
              </div>
            )}

            {/* Evaluation & Grade - English */}
            {(formData.language === 'English' || formData.language === 'Français + English') && (
              <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#2c3e50', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '10px', display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '8px' }}>⭐</span> Evaluation & Grade {formData.language === 'Français + English' && '(English)'}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                  <select name="mentionEn" value={formData.mentionEn} onChange={handleChange} required style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }}>
                    <option value="">-- Select distinction --</option>
                    <option value="Very Honorable with congratulations from the jury">Very Honorable with congratulations from the jury</option>
                    <option value="Very Honorable">Very Honorable</option>
                    <option value="Honorable">Honorable</option>
                    <option value="Good">Good</option>
                    <option value="Fairly Good">Fairly Good</option>
                    <option value="custom">Customize...</option>
                  </select>
                  {formData.mentionEn === 'custom' && (
                    <input type="text" name="mentionEnCustom" value={formData.mentionEnCustom || ''} onChange={handleChange} placeholder="Custom Distinction (English) *" required style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }} />
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: formData.gradePresetEn === 'custom' || (formData.gradeTexteEn && !['Bachelor', 'Doctor (PhD)', 'Doctor (Ph.D)', 'Master (M.Sc)', 'Master (M.A)', 'HDR'].includes(formData.gradeTexteEn)) ? '1fr 1fr' : '1fr', gap: '20px', marginTop: '20px' }}>
                  <select name="gradePresetEn" value={formData.gradePresetEn || ''} onChange={handleChange} required style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }}>
                    <option value="">-- Select degree --</option>
                    <option value="Bachelor">Bachelor</option>
                    <option value="Master (M.Sc)">Master (M.Sc)</option>
                    <option value="Master (M.A)">Master (M.A)</option>
                    <option value="Doctor (PhD)">Doctor (PhD)</option>
                    <option value="Doctor (Ph.D)">Doctor (Ph.D)</option>
                    <option value="HDR">HDR</option>
                    <option value="custom">Customize...</option>
                  </select>
                  {(formData.gradePresetEn === 'custom' || (formData.gradeTexteEn && !['Bachelor', 'Doctor (PhD)', 'Doctor (Ph.D)', 'Master (M.Sc)', 'Master (M.A)', 'HDR'].includes(formData.gradeTexteEn))) && (
                    <input type="text" name="gradeTexteEn" value={formData.gradeTexteEn || ''} onChange={handleChange} placeholder="Custom degree *" required style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }} />
                  )}
                </div>
              </div>
            )}

            {/* Final Details */}
            <div style={{ marginBottom: '32px', padding: '24px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <h3 style={{ fontSize: '17px', fontWeight: '600', color: '#2c3e50', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '10px', display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px' }}>📅</span> Détails de Publication
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                <input type="text" name="dateEmission" value={formData.dateEmission} onChange={handleChange} placeholder="Date d'émission (jj/mm/aaaa) *" required style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', fontSize: '14px', border: '1px solid #ced4da', borderRadius: '6px', background: 'white' }} />
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', padding: '16px', background: 'white', borderRadius: '6px', cursor: 'pointer', border: '1px solid #ced4da', transition: 'all 0.2s' }}>
                  <input type="checkbox" name="includeSignature" checked={formData.includeSignature} onChange={(e) => setFormData((p) => ({ ...p, includeSignature: e.target.checked }))} style={{ marginRight: '12px', width: '18px', height: '18px', cursor: 'pointer' }} />
                  <span style={{ fontSize: '14px', color: '#495057', fontWeight: '500' }}>Inclure la signature du Recteur</span>
                </label>
              </div>
            </div>

            <button type="submit" className="submit-button" style={{ width: '100%', background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', color: 'white', border: 'none', padding: '16px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginTop: '8px', boxShadow: '0 4px 6px rgba(40, 167, 69, 0.2)', transition: 'all 0.3s' }} onMouseOver={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 12px rgba(40, 167, 69, 0.3)'; }} onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 6px rgba(40, 167, 69, 0.2)'; }}>
              ✓ Créer l'Attestation de Grade
            </button>
          </form>
        </div>

        {/* Search + Sort */}
        <div className="attestation-search-bar" style={{ display: 'flex', gap: 10, margin: '15px 0', alignItems: 'center', padding: 12, background: '#f8f9fa', borderRadius: 8, border: '1px solid #dee2e6' }}>
          <input type="text" placeholder="Rechercher par nom..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ flex: 1, padding: '10px 12px', border: '1px solid #ced4da', borderRadius: 6 }} />
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ padding: '10px 12px', border: '1px solid #ced4da', borderRadius: 6 }}>
            <option value="date">📅 Trier par date</option>
            <option value="name">📝 Trier par nom</option>
          </select>
        </div>

        {/* List */}
        <div className="aquidedroit-document-list">
          {documents
            .filter((d) => d.noms?.toLowerCase().includes(searchQuery.toLowerCase()))
            .sort((a, b) => (sortBy === 'date' ? new Date(b.createdAt || 0) - new Date(a.createdAt || 0) : (a.noms || '').localeCompare(b.noms || '')))
            .map((doc) => (
              <div key={doc.id} className="aquidedroit-document-item">
                <button className="aquidedroit-document-toggle" onClick={() => showModal(doc)}>
                  <Eye size={16} style={{ marginRight: 8 }} />
                  {doc.noms} - Attestation de Grade
                </button>
                <Popconfirm title="Supprimer le document" description="Êtes-vous sûr de vouloir supprimer ce document ?" onConfirm={() => delDoc(doc.id)} okText="Oui" cancelText="Non" okButtonProps={{ danger: true }}>
                  <button className="aquidedroit-delete-btn"><Delete size={15} /></button>
                </Popconfirm>
              </div>
            ))}
        </div>
      </div>

      {/* Modal */}
      <ConfigProvider>
        <Modal
          title={<span style={{ color: 'white' }}>Aperçu - Attestation de Grade</span>}
          open={modalVisible}
          onCancel={closeModal}
          width="95vw"
          style={{ top: 10 }}
          centered={false}
          maskClosable={true}
          keyboard={true}
          footer={[
            <Button key="download" type="primary" icon={<Download size={16} />} onClick={downloadPDF} style={{ backgroundColor: '#28a745', borderColor: '#28a745', fontWeight: 'bold' }}>Télécharger PDF</Button>,
            <Button key="close" onClick={closeModal} style={{ fontWeight: 'bold' }}>Fermer</Button>,
          ]}
        >
          {selectedDocument && (
            <div className="aquidedroit-modal-content">
              <AttestationGradeDocument document={selectedDocument} qrCodeData={qrCodeData} />
            </div>
          )}
        </Modal>
      </ConfigProvider>
    </div>
  );
};

const AttestationGradeDocument = ({ document: d, qrCodeData }) => {
  const formatDateWithMonthName = (dateStr, lang = 'fr') => {
    if (!dateStr) return '';
    
    // Handle dd/mm/yyyy format
    let day, month, year;
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10) - 1; // months are 0-indexed
      year = parseInt(parts[2], 10);
    } else {
      // Fallback for ISO format (yyyy-mm-dd)
      const date = new Date(dateStr);
      day = date.getDate();
      month = date.getMonth();
      year = date.getFullYear();
    }
    
    const monthsFr = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
    const monthsEn = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const months = lang === 'en' ? monthsEn : monthsFr;
    return `${day} ${months[month]} ${year}`;
  };

  const language = d.language || 'Français';
  const showBoth = language === 'Français + English';

  return (
    <div id="attestation-grade-document">
      {(language === 'Français' || showBoth) && (
        <AttestationGradeFrench document={d} qrCodeData={qrCodeData} formatDateWithMonthName={formatDateWithMonthName} showBoth={showBoth} />
      )}
      {(language === 'English' || showBoth) && (
        <AttestationGradeEnglish document={d} qrCodeData={qrCodeData} formatDateWithMonthName={formatDateWithMonthName} showBoth={showBoth} />
      )}
    </div>
  );
};

const AttestationGradeFrench = ({ document: d, qrCodeData, formatDateWithMonthName, showBoth }) => {
  return (
    <div className="aquidedroit-document" style={showBoth ? { pageBreakAfter: 'always' } : {}}>
      {/* Header (same as A QUI DE DROIT) */}
      <div className="aquidedroit-header">
        <div className="aquidedroit-header-row">
          <div className="aquidedroit-left-logo">
            <img src={unilislogo} alt="Logo UNILIS" className="aquidedroit-logo" />
            <div className="aquidedroit-left-text">
              <h1 className="aquidedroit-school">ECOLE DOCTORALE</h1>
              <h2 className="aquidedroit-subtitle">Cotutelle Internationale de Thèse et Co-diplomation</h2>
            </div>
          </div>
          <div className="aquidedroit-header-center">
            <h3 className="aquidedroit-university">UNIVERSITE DE LISALA</h3>
            <p className="aquidedroit-center">Centre Interuniversitaire de Recherche Pluridisciplinaire (Cirep)</p>
            <p className="aquidedroit-status">Status: Universite publique</p>
            <p className="aquidedroit-web">Web: www.cirep.ac.cd</p>
            <p className="aquidedroit-email">E-mail: info@cirep.ac.ad</p>
          </div>
          <div className="aquidedroit-right-logo">
            <img src={edaplogo} alt="Logo EDAP" className="aquidedroit-logo" />
          </div>
        </div>
      </div>

      {/* Separator */}
      <div className="aquidedroit-separator"></div>

      {/* No/réf */}
      {d.numeroRef && (
        <div className="aquidedroit-number-ref">
          <span>No/réf {d.numeroRef}</span>
        </div>
      )}

      {/* Title */}
      <div className="aquidedroit-title-section">
        <h1 className="aquidedroit-main-title">ATTESTATION DE {d.diplomeType.toUpperCase()}</h1>
      </div>

      {/* Body */}
      <div className="aquidedroit-document-body">
        <div className="aquidedroit-main-text">
          <p>
            Le Recteur de l'Universitė de Lisala,<br/>
            Vu les accords de partenariats de cotutelle et codiplomation signés entre le Centre Interuniversitaire de<br/>
            Recherche Pluridisciplinaire(CIREP) de l'Université de Lisala en RDC et l'UNCR University aux Etats Unis ;<br/>
            soussignė, certifie que <strong>{d.noms}</strong>; {d.genre === 'F' ? 'Née' : 'Né'} le <strong>{formatDateWithMonthName(d.dateNaissance, 'fr')}</strong> à <strong>{d.lieuNaissance}</strong> {d.paysConnector || 'au'} <strong>{d.pays}</strong>; {d.genre === 'F' ? 'inscrite' : 'inscrit'} à l'Universitė de Lisala en cotutelle et codiplomation avec l'UNCR University sous le numéro <strong>{d.matricule}</strong>; a satisfait<br/>
            aux ėpreuves sanctionnant la fin du Cycle <strong>{d.cycleDescription}</strong> en <strong>{d.filiere}</strong>; Spécialité : <strong>{d.specialite}</strong>. {d.genre === 'F' ? 'Elle' : 'Il'} a soutenu {d.diplomeType === 'Doctorat' ? 'la Thèse' : 'le Mémoire'} « <strong>{d.theseTitre}</strong> » {d.diplomeType !== 'Licence' ? 'soutenue en présentiel devant' : 'devant'}<br/>
            un jury composé de:<br/>
            Président : <strong>{d.president}</strong><br/>
            Directeur : <strong>{d.directeur}</strong><br/>
            Rapporteur : <strong>{d.rapporteur}</strong><br/>
            {Array.isArray(d.membres) && d.membres.filter(Boolean).map((m, i) => (
              <span key={i}>Membre : <strong>{m}</strong><br/></span>
            ))}
            <br/>
            Considérant qu'à la suite de {d.genre === 'F' ? 'son' : 'son'} brillant exposé et après en avoir délibéré, le jury a décidé à l'unanimité,
            d'accepter le travail de recherche de <strong>{d.noms}</strong> avec la mention <strong>{d.mention}</strong>.<br/>
            {d.diplomeType === 'Licence' ? (
              <>Vu l'avis favorable du jury attestant que {d.genre === 'F' ? "l'intéressée" : "l'intéressé"} a satisfait au contrôle des compétences prévues par les textes légaux et réglementaires;<br/></>
            ) : (
              <>Vu l'avis favorable de l'Ecole Doctorale en cotutelle et codiplomation avec l'UNCR University attestant que
              {d.genre === 'F' ? " l'intéressée" : " l'intéressé"} a satisfait au contrôle des compétences prévues par les textes légaux et réglementaires; <br/></>
            )}
            Confirme que le Grade de <strong>{d.gradeTexte}</strong> est décerné à {d.genre === 'F' ? "l'impétrante" : "l'impétrant"} pour servir et valoir ce que de droit.<br/>
            Lisala , le <strong>{formatDateWithMonthName(d.dateEmission, 'fr')}</strong>
          </p>
        </div>
      </div>

      {/* Signature (only the Recteur) */}
      <div className="aquidedroit-signature">
        {d.includeSignature && (
          <div className="aquidedroit-signature-content">
            <img src={rectorSig} alt="Signature du Recteur" className="aquidedroit-signature-image" />
            <p className="aquidedroit-signature-name">Professeur Abbé Donat TEBAKABE Alomo</p>
            <p className="aquidedroit-signature-title">Recteur</p>
          </div>
        )}
        {qrCodeData && !showBoth && (
          <div className="aquidedroit-qr-section">
            <img src={qrCodeData} alt="QR Code - Document Verification" className="qr-code" style={{ width: '60px', height: '60px', border: '1px solid #000' }} />
          </div>
        )}
      </div>
    </div>
  );
};

const AttestationGradeEnglish = ({ document: d, qrCodeData, formatDateWithMonthName, showBoth }) => {
  // Map French diploma types to English
  const diplomaTypeMap = {
    'Licence': 'Bachelor',
    'Master': 'Master',
    'Doctorat': 'Doctorate',
    'HDR': 'HDR'
  };

  return (
    <div className="aquidedroit-document">
      {/* Header */}
      <div className="aquidedroit-header">
        <div className="aquidedroit-header-row">
          <div className="aquidedroit-left-logo">
            <img src={unilislogo} alt="Logo UNILIS" className="aquidedroit-logo" />
            <div className="aquidedroit-left-text">
              <h1 className="aquidedroit-school">DOCTORAL SCHOOL</h1>
              <h2 className="aquidedroit-subtitle">International Joint Supervision and Joint Degree</h2>
            </div>
          </div>
          <div className="aquidedroit-header-center">
            <h3 className="aquidedroit-university">UNIVERSITY OF LISALA</h3>
            <p className="aquidedroit-center">Interuniversity Center for Multidisciplinary Research (Cirep)</p>
            <p className="aquidedroit-status">Status: Public University</p>
            <p className="aquidedroit-web">Web: www.cirep.ac.cd</p>
            <p className="aquidedroit-email">E-mail: info@cirep.ac.ad</p>
          </div>
          <div className="aquidedroit-right-logo">
            <img src={edaplogo} alt="Logo EDAP" className="aquidedroit-logo" />
          </div>
        </div>
      </div>

      {/* Separator */}
      <div className="aquidedroit-separator"></div>

      {/* No/réf */}
      {d.numeroRef && (
        <div className="aquidedroit-number-ref">
          <span>No/ref {d.numeroRef}</span>
        </div>
      )}

      {/* Title */}
      <div className="aquidedroit-title-section">
        <h1 className="aquidedroit-main-title">AWARD OF UNIVERSITY DEGREE</h1>
      </div>

      {/* Body */}
      <div className="aquidedroit-document-body">
        <div className="aquidedroit-main-text">
          <p>
            The Rector of the University of Lisala,<br/><br/>
            Having regard to the joint supervision and joint degree partnership agreements signed between the Interuniversity Center for Multidisciplinary Research (CIREP) of the University of Lisala in the DRC and UNCR University in the United States;<br/>
            certifies that the undersigned <strong>{d.noms}</strong>; Born on <strong>{formatDateWithMonthName(d.dateNaissance, 'en')}</strong>, in <strong>{d.lieuNaissance}</strong>, <strong>{d.pays}</strong>; enrolled at the University of Lisala in joint supervision and co-degree with UNCR University under number <strong>{d.matricule}</strong>; has successfully completed the exams marking the end of the {d.diplomeType === 'Doctorat' ? 'three-year doctoral program' : `${d.cycleDescription} program`} in <strong>{d.filiereEn || d.filiere}</strong>; Specialization: <strong>{d.specialiteEn || d.specialite}</strong>.<br/>
            {d.genre === 'F' ? 'She' : 'He'} defended {d.genre === 'F' ? 'her' : 'his'} {d.diplomeType === 'Doctorat' ? 'thesis' : 'dissertation'} entitled "<strong>{d.theseTitreEn || d.theseTitre}</strong>" in person before a panel composed of:<br/><br/>
            Chair: <strong>{d.president}</strong><br/>
            Director: <strong>{d.directeur}</strong><br/>
            Rapporteur: <strong>{d.rapporteur}</strong><br/>
            {Array.isArray(d.membres) && d.membres.filter(Boolean).map((m, i) => (
              <span key={i}>Member: <strong>{m}</strong><br/></span>
            ))}
            <br/>
            Considering that, following {d.genre === 'F' ? 'her' : 'his'} brilliant presentation and after deliberation, the panel unanimously decided to accept the research work of <strong>{d.noms}</strong> with the grade of <strong>{d.mentionEn === 'custom' ? (d.mentionEnCustom || d.mention) : (d.mentionEn || d.mention)}</strong>.<br/>
            Given the favorable report of the panel attesting that the candidate has satisfied the requirements set forth in the legal and regulatory texts;<br/>
            Confirms that the degree of <strong>{d.gradeTexteEn || d.gradeTexte}</strong> is awarded to the candidate to serve and be valid as required by law.<br/>
            Lisala, <strong>{formatDateWithMonthName(d.dateEmission, 'en')}</strong>
          </p>
        </div>
      </div>

      {/* Signature */}
      <div className="aquidedroit-signature">
        {d.includeSignature && (
          <div className="aquidedroit-signature-content">
            <img src={rectorSig} alt="Rector's Signature" className="aquidedroit-signature-image" />
            <p className="aquidedroit-signature-name">Professor Abbé Donat TEBAKABE Alomo</p>
            <p className="aquidedroit-signature-title">Rector</p>
          </div>
        )}
        {qrCodeData && (
          <div className="aquidedroit-qr-section">
            <img src={qrCodeData} alt="QR Code - Document Verification" className="qr-code" style={{ width: '60px', height: '60px', border: '1px solid #000' }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AttestationGrade;
